<script setup lang="ts">
import { Head, router, useForm } from '@inertiajs/vue3'
import { LoaderCircle } from 'lucide-vue-next'
import { onMounted, onUnmounted, ref, watch } from 'vue'

import InputError from '@/components/InputError.vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import AuthLayout from '@/layouts/AuthLayout.vue'
import { newPortalSessionPath, portalAuthOtpCancelPath, portalAuthOtpPath, portalAuthOtpVerifyPath } from '@/routes'

const props = defineProps<{
  actor_type: string
  otp_pending: boolean
  otp_email: string
  otp_error?: string
  otp_resend_available_at?: number | null
}>()

// Step 1: email form state
const activeTab = ref(props.actor_type)
const email = ref('')
const sendProcessing = ref(false)
const sendError = ref<string | null>(null)

function submitEmail() {
  if (!email.value.trim()) {
    sendError.value = 'Email is required'
    return
  }
  sendProcessing.value = true
  sendError.value = null
  router.post(
    portalAuthOtpPath(),
    { email: email.value, actor_type: activeTab.value },
    {
      onError: () => {
        sendError.value = 'Something went wrong. Please try again.'
      },
      onFinish: () => {
        sendProcessing.value = false
      },
    },
  )
}

// Resend cooldown countdown
const resendCountdown = ref(0)
let countdownInterval: ReturnType<typeof setInterval> | null = null

function updateCountdown() {
  if (!props.otp_resend_available_at) {
    resendCountdown.value = 0
    return
  }
  const remaining = Math.ceil(props.otp_resend_available_at - Date.now() / 1000)
  resendCountdown.value = remaining > 0 ? remaining : 0
  if (resendCountdown.value === 0 && countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

function startCountdown() {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
  updateCountdown()
  if (resendCountdown.value > 0) {
    countdownInterval = setInterval(updateCountdown, 1000)
  }
}

onMounted(startCountdown)
onUnmounted(() => {
  if (countdownInterval) clearInterval(countdownInterval)
})
watch(() => props.otp_resend_available_at, startCountdown)

// Step 2: OTP verify form state
const otpForm = useForm({ otp_code: '' })

function submitOtp() {
  otpForm.post(portalAuthOtpVerifyPath())
}

function resend() {
  router.post(portalAuthOtpPath(), { email: props.otp_email, actor_type: props.actor_type })
}

function cancel() {
  router.delete(portalAuthOtpCancelPath())
}
</script>

<template>
  <Head :title="otp_pending ? 'Enter your code' : 'Sign in'" />

  <!-- OTP verify step -->
  <AuthLayout
    v-if="otp_pending"
    title="Enter your code"
    description="Check your email (and phone) for the sign-in code"
    :logo-href="newPortalSessionPath()"
  >
    <div class="flex flex-col gap-6">
      <p class="text-muted-foreground text-center text-sm">
        We sent a code to <strong>{{ otp_email }}</strong
        >.
      </p>

      <form class="flex flex-col items-center gap-4" @submit.prevent="submitOtp">
        <InputOTP v-model="otpForm.otp_code" :maxlength="6">
          <InputOTPGroup>
            <InputOTPSlot class="h-12 w-14 text-lg font-bold" :index="0" />
            <InputOTPSlot class="h-12 w-14 text-lg font-bold" :index="1" />
            <InputOTPSlot class="h-12 w-14 text-lg font-bold" :index="2" />
            <InputOTPSlot class="h-12 w-14 text-lg font-bold" :index="3" />
            <InputOTPSlot class="h-12 w-14 text-lg font-bold" :index="4" />
            <InputOTPSlot class="h-12 w-14 text-lg font-bold" :index="5" />
          </InputOTPGroup>
        </InputOTP>

        <InputError :messages="otp_error ? [otp_error] : undefined" class="w-full text-center" />
        <InputError :messages="otpForm.errors.otp_code" class="w-full text-center" />

        <Button type="submit" class="w-full mt-4" :disabled="otpForm.otp_code.length < 6 || otpForm.processing">
          <LoaderCircle v-if="otpForm.processing" class="size-4 animate-spin" />
          Verify code
        </Button>
      </form>

      <div class="flex justify-center gap-4 text-sm">
        <button
          type="button"
          :disabled="resendCountdown > 0"
          :class="[
            'underline-offset-4',
            resendCountdown > 0
              ? 'text-muted-foreground cursor-not-allowed opacity-50'
              : 'text-muted-foreground hover:text-foreground underline',
          ]"
          @click="resend"
        >
          {{ resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend code' }}
        </button>
        <span class="text-muted-foreground">·</span>
        <button
          type="button"
          class="text-muted-foreground hover:text-foreground underline underline-offset-4"
          @click="cancel"
        >
          Use different email
        </button>
      </div>
    </div>
  </AuthLayout>

  <!-- Email form step -->
  <AuthLayout
    v-else
    title="Portal Sign In"
    description="Enter your email to receive a sign-in link and code"
    :logo-href="newPortalSessionPath()"
  >
    <div class="flex flex-col gap-6">
      <Tabs v-model="activeTab">
        <TabsList class="w-full">
          <TabsTrigger value="organization" class="flex-1">Organization</TabsTrigger>
          <TabsTrigger value="partner" class="flex-1">Partner</TabsTrigger>
          <TabsTrigger value="customer" class="flex-1">Customer</TabsTrigger>
        </TabsList>

        <TabsContent v-for="type in ['organization', 'partner', 'customer']" :key="type" :value="type">
          <form class="mt-4 flex flex-col gap-4" @submit.prevent="submitEmail">
            <div class="grid gap-2">
              <Label for="email">Email address</Label>
              <Input id="email" v-model="email" type="email" placeholder="you@example.com" autofocus required />
              <InputError v-if="sendError" :messages="[sendError]" />
            </div>
            <Button type="submit" class="w-full" :disabled="sendProcessing">
              <LoaderCircle v-if="sendProcessing" class="size-4 animate-spin" />
              Send sign-in link &amp; code
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  </AuthLayout>
</template>
